import type { FC, RefObject } from 'react';
import React, { useMemo, useState, useEffect, useRef } from 'react';

import { defaultHandler } from './field/config';
import { actions } from './classes';
import { Field } from './field';
import { createSetState, showGender } from './helpers/actions';
import { genders, hasMiddleNameDefault, genderDefault, defaultState } from './config';
import type { TFioField } from './models';

export const Fio: FC<TFioField> = ({
    Checkbox,
    RadioTags,
    analytics,
    className,
    firstName,
    firstNameRef,
    gender,
    genderRef,
    genderSettings = {},
    lastName,
    lastNameRef,
    middleName,
    middleNameRef: middleNameRefInit,
    hasMiddleNameSettings = {},
    onBlur = defaultHandler,
    onChange = defaultHandler,
    onClick = defaultHandler,
    onCancel = defaultHandler,
    onComplete = defaultHandler,
    onFocus = defaultHandler,
    onSelect = defaultHandler,
    withGender,
    dataTestId = 'form-fio',
    ...rest
}) => {
    const [hideGender, setHideGender] = useState(true);
    const [state, stateSetter] = useState(defaultState);
    const setState = createSetState(stateSetter);
    const middleNameRefFallback = useRef<HTMLInputElement>(null);
    const middleNameRef = middleNameRefInit || middleNameRefFallback;
    const settings = {
        gender: { ...genderDefault, ...genderSettings },
        hasMiddleName: { ...hasMiddleNameDefault, ...hasMiddleNameSettings },
    };
    const fields = useMemo(
        () => ({ firstName, gender, lastName, middleName }),
        [firstName, gender, lastName, middleName]
    );

    const Scenario = withGender ? actions.WithGender : actions.Base;

    const action = new Scenario({
        analytics,
        cb: {
            onBlur,
            onCancel,
            onChange,
            onClick,
            onFocus,
            onSelect,
            onComplete,
        },
        fields,
        middleNameRef,
        setState,
        state,
    });

    useEffect(() => {
        if (!withGender) return;
        if (hideGender && showGender(fields, state)) setHideGender(false);
    }, [fields, hideGender, state, withGender]);

    return (
        <>
            <Field
                {...rest}
                {...action.makeProps('lastName')}
                dataTestId={dataTestId + '-last-name'}
                forwardedRef={lastNameRef}
                styler={{ marginBottom: 24 }}
            />
            <Field
                {...rest}
                {...action.makeProps('firstName')}
                dataTestId={dataTestId + '-first-name'}
                forwardedRef={firstNameRef}
                styler={{ marginBottom: 24 }}
            />
            <Field
                {...rest}
                {...action.makeProps('middleName')}
                dataTestId={dataTestId + '-middle-name'}
                disabled={!state.hasMiddleName}
                forwardedRef={middleNameRef}
                styler={{ marginBottom: 16 }}
            />
            <div
                onMouseEnter={action.onMouseEnterCheckbox}
                onMouseLeave={action.onMouseLeaveCheckbox}
            >
                <Checkbox
                    {...settings.hasMiddleName}
                    caption={undefined}
                    checked={!state.hasMiddleName}
                    dataTestId={dataTestId + '-checkbox-middleName'}
                    onChange={action.onCheckbox}
                >
                    {settings.hasMiddleName.caption}
                </Checkbox>
            </div>
            {!hideGender && (
                <>
                    <RadioTags
                        {...settings.gender}
                        asRadioInput
                        error={fields.gender?.meta.invalid && fields.gender.meta.touched}
                        forwardedRef={genderRef as RefObject<HTMLButtonElement>}
                        onSelect={action.onRadioTags}
                        selected={fields.gender?.input.value}
                        styler={{ marginTop: 20 }}
                        tags={genders}
                    />
                </>
            )}
            {withGender && (
                <input
                    name={settings.gender.name}
                    type="hidden"
                    value={fields.gender?.input.value}
                />
            )}
        </>
    );
};
